import { getConciliationAmountLimit } from '../../utils/conciliationAmountLimit'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import TablaAdmVinculacion from './TableAdmVinculacion'
import GlobalContext from '../../../context/global-context'
import dayjs from 'dayjs'
import TableCajaPrincipal from './TableCajaPrincipal'
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json'
import Lottie from 'lottie-react'
import { BankAPI } from '../../../api/bancos.api'
import { useInfiniteMovements } from '../../../hooks/useInfiniteMovements'
import { useInfiniteBankTransactions } from '../../../hooks/useInfiniteBankTransactions'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import clsx from 'clsx'
import { currentYear } from '../../utils/constant'
import FilterDateConciliar from '../table/FilterDateConciliar'
import TablaCompensacion from './TablaCompensacion'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'
import { useCompensacionData, getTypeByStatus } from './hooks/useCompensacionData'
import { useLinkageFilters } from './hooks/useLinkageFilters'
import businessApi from '../../../api/business.api'
import TablaVinculacionesAnticipo from './TablaVinculacionesAnticipo'
import { isPreviousYearConciliationLinkage } from '../../utils/normalizeConciliationLinkage'

const ENABLE_DIN_ANTICIPO = true

const getRequiredBankMovementType = (status, documentType) => {
  const normalizedDocumentType = String(documentType || '').trim().toLowerCase()
  if (normalizedDocumentType === 'venta') return 'abono'
  if (normalizedDocumentType === 'compra' || normalizedDocumentType === 'honorario') return 'cargo'

  const normalizedStatus = String(status || '').trim().toUpperCase()
  if (normalizedStatus.includes('COBR')) return 'abono'
  if (normalizedStatus.includes('PAG')) return 'cargo'
  return null
}

const getBankMovementType = row => {
  const explicitType = String(
    row?.tipoMovimiento ?? row?.transactionType ?? row?.tipo ?? ''
  ).trim().toLowerCase()
  if (explicitType === 'abono' || explicitType === 'cargo') return explicitType

  const abono = Number(row?.montoAbono ?? 0)
  const cargo = Number(row?.montoCargo ?? 0)
  if (abono > 0 && cargo <= 0) return 'abono'
  if (cargo > 0 && abono <= 0) return 'cargo'

  const movementTable = [row?.tabla, row?.tablaPrincipal, row?.origenTabla]
    .map(Number)
    .find(value => value === 6 || value === 7)
  if (movementTable === 6) return 'abono'
  if (movementTable === 7) return 'cargo'
  return null
}

const InputField = ({ label, placeholder, type, value, onChange }) => (
  <div className='mt-1'>
    <label className='block mb-[1px] text-[11px] font-small font-medium leading-tight'>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className=' h-7 block w-[150px] rounded-full border-gray-300 sm:text-[11px] font-medium'
    />
  </div>
)

const PAGINATION_LIMIT = 10000

const ListEditVincular = ({
  type: documentType,
  idPrincipal,
  dataLinkage,
  setReloadData,
  reloadData,
  selectedIds,
  setSelectedIds,
  dataModal,
  modalStatus,
  setInitialSelectedIds,
  unselectedIds,
  setUnselectedIds,
  onCurrentDataBanco,
  onCurrentDataCaja,
  onCurrentDataGestion,
  onCurrentDataTC,
  selectedBank,
  setSelectedBank,
  origenSeleccionado,
  setOrigenSeleccionado,
  setUserInteracted,
  selectedCheckboxes,
  setSelectedCheckboxes,
  selectedDataByOrigen,
  setSelectedDataByOrigen,
  getRowId,
  esDin = false,
  conciliarConAnticipo = false,
  setConciliarConAnticipo
}) => {
  const toArr = v => (Array.isArray(v) ? v : [])
  const safeOrigen = toArr(origenSeleccionado)
  const [value, setValue] = useState([null, null])
  const [, setDebouncedValue] = useState([null, null])
  const { currentBusiness } = useContext(GlobalContext)
  const [monthFilter, setMonthFilter] = useState(0)
  const [yearFilter, setYearFilter] = useState(currentYear)
  const [filters] = useState({
    fechaInicio: dayjs().startOf('year').format('YYYY-MM-DD'),
    fechaFin: dayjs().format('YYYY-MM-DD')
  })
  const [tempFilters, setTempFilters] = useState({ ...filters })
  const [searchKeyword, setSearchKeyword] = useState('')
  const [valoresAprox, setValoresAprox] = useState(false)
  const bankAPI = new BankAPI()
  const [dataBanco, setDataBanco] = useState({})
  const [porcentajeRange] = useState([0, 100])
  const [showFilters, setShowFilters] = useState(true)
  const [page] = useState(0)
  const [rowsPerPage] = useState(-1)
  const [, setForceShowAll] = useState(false)
  const [order, setOrder] = useState({ col: 'Fecha', field: 'fecha', direction: 'asc' })
  const businessId = currentBusiness?.id || 0
  const initializedOriginKeyRef = useRef(null)
  const [vinculacionesAnticipo, setVinculacionesAnticipo] = useState([])

  const vinculacionesAnticipoVisibles = useMemo(() => {
    const linkageIds = new Set(
      toArr(dataModal?.linkage)
        .map(item => Number(item?.idPrincipal))
        .filter(id => Number.isFinite(id))
    )
    const anticiposAsociados = vinculacionesAnticipo.filter(row =>
      linkageIds.has(Number(row?.idPrincipal))
    )

    return anticiposAsociados
  }, [dataModal?.linkage, vinculacionesAnticipo])

  const movementType = getTypeByStatus(modalStatus) || 'ingreso'
  const requiredBankMovementType = getRequiredBankMovementType(modalStatus, documentType)
  const {
    data: infiniteData,
    fetchNextPage: fetchNextBankMovements,
    hasNextPage: hasNextBankMovements,
    isFetchingNextPage: isFetchingNextBankMovements,
    isLoading: isLoadingData,
    refetch: refetchBankMovements
  } = useInfiniteBankTransactions({
    businessId,
    productId: 0,
    year: yearFilter,
    month: 0,
    limit: PAGINATION_LIMIT,
    filters: {
      ...(requiredBankMovementType
        ? { transactionType: requiredBankMovementType.toUpperCase() }
        : {}),
      from: tempFilters.fechaInicio,
      to: tempFilters.fechaFin
    },
    enabled: safeOrigen.includes('banco')
  })

  const {
    data: egresosData,
    fetchNextPage: fetchNextEgresos,
    hasNextPage: hasNextEgresos,
    isFetchingNextPage: isFetchingNextEgresos
  } = useInfiniteMovements({
    businessId,
    year: yearFilter,
    month: monthFilter,
    linkage: true,
    limit: PAGINATION_LIMIT,
    filters: { type: 'egreso' },
    enabled: safeOrigen.includes('tc')
  })

  const {
    data: ingresosData,
    fetchNextPage: fetchNextIngresos,
    hasNextPage: hasNextIngresos,
    isFetchingNextPage: isFetchingNextIngresos
  } = useInfiniteMovements({
    businessId,
    year: yearFilter,
    month: monthFilter,
    linkage: true,
    limit: PAGINATION_LIMIT,
    filters: { type: 'ingreso' },
    enabled: safeOrigen.includes('tc')
  })

  const filtrar = (items = []) => (Array.isArray(items) ? items : [])
  const type = movementType

  // Datos crudos desde los infinite queries
  const allEgresos = egresosData?.pages.flatMap(p => p.data) ?? []
  const allIngresos = ingresosData?.pages.flatMap(p => p.data) ?? []

  // Aplicas la misma lógica de filtros que usabas antes
  const egresoFiltrado = filtrar(allEgresos)
  const ingresoFiltrado = filtrar(allIngresos)

  let seleccionado = type

  // Si type = ingreso → usar egresos si ingreso está vacío
  if (type === 'ingreso' && ingresoFiltrado.length === 0 && egresoFiltrado.length > 0) {
    seleccionado = 'egreso'
  }

  // Si type = egreso → usar ingresos si egreso está vacío
  else if (type === 'egreso' && egresoFiltrado.length === 0 && ingresoFiltrado.length > 0) {
    seleccionado = 'ingreso'
  }

  // Fallback si por alguna razón nada de lo anterior aplica
  if (!seleccionado) {
    seleccionado =
      ingresoFiltrado.length > 0 ? 'ingreso' : egresoFiltrado.length > 0 ? 'egreso' : 'ingreso'
  }

  const dataCompensacion = useCompensacionData({
    egresoFiltrado,
    ingresoFiltrado,
    dataModal,
    seleccionado
  })

  const hasMoreCompPages = hasNextEgresos || hasNextIngresos
  const isFetchingMoreCompensacion = isFetchingNextEgresos || isFetchingNextIngresos
  const handleFetchMoreCompensacion = useCallback(() => {
    if (hasNextEgresos) fetchNextEgresos()
    if (hasNextIngresos) fetchNextIngresos()
  }, [hasNextEgresos, hasNextIngresos, fetchNextEgresos, fetchNextIngresos])

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage: hasMoreCompPages,
    isFetchingNextPage: isFetchingMoreCompensacion,
    fetchNextPage: handleFetchMoreCompensacion
  })

  const allData = useMemo(() => {
    return infiniteData?.pages?.flatMap(page => page.data) || []
  }, [infiniteData])
  const data = requiredBankMovementType
    ? allData.filter(row => getBankMovementType(row) === requiredBankMovementType)
    : allData
  const [collapsed, setCollapsed] = useState({})
  const isCollapsed = key => Boolean(collapsed?.[key])

  // alternar colapso/expansión
  const toggleSection = key => {
    setCollapsed(prev => {
      const next = {}
      ;['banco', 'tc', 'anticipo', 'caja-gestion'].forEach(k => {
        next[k] = k !== key
      })
      if (!prev[key]) {
        next[key] = true
      } else {
        next[key] = false
      }
      return next
    })
  }

  const getBanco = async () => {
    try {
      if (!currentBusiness?.id) return
      const responseBank = await bankAPI.getListBank(currentBusiness.id)
      setDataBanco(responseBank)
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    getBanco()
  }, [currentBusiness])

  const bankOptions = [
    'Caja Principal',
    ...(Array.isArray(dataBanco?.data)
      ? dataBanco.data
          .map(item => item.bank?.name)
          .filter(name => name && name !== 'Caja Principal')
          .filter((value, index, self) => self.indexOf(value) === index)
      : [])
  ]

  useEffect(() => {
    if (!ENABLE_DIN_ANTICIPO || !currentBusiness?.id) {
      setVinculacionesAnticipo([])
      return
    }

    let active = true
    const getVinculacionesAnticipo = async () => {
      try {
        const response = await businessApi.getListSummaryVinculos({
          businessId: currentBusiness.id,
          month: 0,
          year: Number(yearFilter),
          linkage: undefined,
          limit: 10000
        })
        const payload = response?.data
        const rows = Array.isArray(payload)
          ? payload
          : payload && typeof payload === 'object'
            ? Object.values(payload).flat()
            : []
        if (active) {
          setVinculacionesAnticipo(rows.filter(row => [27, 28].includes(Number(row?.folioCredito))))
        }
      } catch (error) {
        console.error('[Conciliar] Error al consultar anticipos de vinculaciones', error)
        if (active) setVinculacionesAnticipo([])
      }
    }

    getVinculacionesAnticipo()
    return () => {
      active = false
    }
  }, [currentBusiness?.id, yearFilter])

  useEffect(() => {
    if (!ENABLE_DIN_ANTICIPO || vinculacionesAnticipo.length === 0) return

    const linkageIds = new Set(
      toArr(dataModal?.linkage)
        .map(item => Number(item?.idPrincipal))
        .filter(id => Number.isFinite(id))
    )
    if (linkageIds.size === 0) return

    const anticiposVinculados = vinculacionesAnticipo.filter(row =>
      linkageIds.has(Number(row?.idPrincipal))
    )
    if (anticiposVinculados.length === 0) return

    const anticipoIds = anticiposVinculados.map(row => row.idPrincipal)
    const anticipoRows = anticiposVinculados.map(row => ({ ...row, _origen: 'anticipo' }))

    setConciliarConAnticipo?.(true)
    setOrigenSeleccionado(prev => (prev.includes('anticipo') ? prev : [...prev, 'anticipo']))
    setSelectedIds(prev => ({ ...prev, anticipo: anticipoIds }))
    setInitialSelectedIds(prev => Array.from(new Set([...toArr(prev), ...anticipoIds])))
    setSelectedDataByOrigen(prev => ({ ...prev, anticipo: anticipoRows }))
    setSelectedCheckboxes(prev => {
      const next = { ...prev }
      anticipoIds.forEach(id => {
        next[id] = true
      })
      return next
    })
  }, [dataModal?.linkage, vinculacionesAnticipo])

  const handleCheckboxChange = (tipo, ids) => {
    // ids siempre es array de números
    const idsArr = toArr(ids).filter(id => Number.isFinite(Number(id)))

    setSelectedIds(prev => ({
      ...prev,
      [tipo]: idsArr
    }))
  }

  const handleSort = ({ col, field }) => {
    const isAsc = order.col === col && order.direction === 'asc'
    setOrder({ col, field, direction: isAsc ? 'desc' : 'asc' })
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, 500)
    return () => {
      clearTimeout(handler)
    }
  }, [value])

  const resetSlider = () => {
    const defaultValue = [1, getConciliationAmountLimit(dataModal?.montoTotal)]
    setValue(defaultValue)
    setDebouncedValue(defaultValue)
  }

  useEffect(() => {
    if (idPrincipal && safeOrigen.includes('banco')) refetchBankMovements()
  }, [idPrincipal, reloadData])

  useEffect(() => {
    if (idPrincipal && !dataCompensacion?.egreso?.length && !dataCompensacion?.ingreso?.length) {
      //
    }
  }, [idPrincipal, modalStatus])

  const handleDateChange = (key, newValue) => {
    const newDate = dayjs(newValue)
    setTempFilters(prev => ({
      ...prev,
      [key]: newDate.format('YYYY-MM-DD')
    }))
    if (key === 'fechaInicio') {
      setMonthFilter(newDate.format('MM'))
      setYearFilter(newDate.format('YYYY'))
    }
  }

  const linkageComprobantes = Array.isArray(dataModal?.linkage)
    ? dataModal.linkage.map(item => item.comprobante)
    : []

  const bankRows = Array.isArray(data) ? [...data] : []
  const bankLinkageRows = toArr(dataModal?.linkage).filter(item => {
    const isBankMovement = [
      item?.libroContableOriginTableId,
      item?.tablaPrincipal,
      item?.tabla
    ].some(value => [6, 7].includes(Number(value)))
    const matchesRequiredType = !requiredBankMovementType ||
      getBankMovementType(item) === requiredBankMovementType
    return isBankMovement && matchesRequiredType
  })

  bankLinkageRows.forEach(linkageItem => {
    const linkageIds = [linkageItem?.id, linkageItem?.idPrincipal]
      .filter(id => id != null)
      .map(String)
    const linkageComprobante = String(linkageItem?.comprobante ?? '').trim()
    const alreadyPresent = bankRows.some(item => {
      const itemIds = [item?.id, item?.idPrincipal].filter(id => id != null).map(String)
      const comprobante = String(item?.comprobante ?? '').trim()
      return (
        (linkageComprobante && comprobante === linkageComprobante) ||
        itemIds.some(id => linkageIds.includes(id))
      )
    })

    if (!alreadyPresent) {
      bankRows.push({
        ...linkageItem,
        id: linkageItem?.id ?? linkageItem?.idPrincipal,
        seleccionado: true
      })
    }
  })

  const isPartialConciliation =
    modalStatus === 'COBRADO' ||
    modalStatus === 'PARCIALMENTE COBRADO' ||
    modalStatus === 'PAGADO' ||
    modalStatus === 'PARCIALMENTE PAGADO' ||
    modalStatus === 'POR COBRAR' ||
    modalStatus === 'POR PAGAR'

  const esBoleta = dataModal?.nombreCuenta?.toLowerCase().includes('boletas')

  const calcPorcentaje = (monto, total) => {
    const diff = Math.abs(monto - total)
    const pct = (1 - diff / total) * 100
    return Math.max(pct, 0)
  }

  const total = esBoleta ? dataModal?.montoNeto || 1 : dataModal?.montoTotal || 1
  let filteredRows = []

  const enrichAndFilter = row => {
    const monto = row.montoAbono || row.montoCargo
    const porcentaje = calcPorcentaje(monto, total)
    return { ...row, porcentaje }
  }

  const applyFilters = rows => {
    const filteredRows = rows.map(enrichAndFilter).filter(item => {
      const matchesKeyword = item.descripcion?.toLowerCase().includes(searchKeyword.toLowerCase())
      const matchesBank = selectedBank
        ? item.nombreCuentaBanco?.trim().toLowerCase() === selectedBank.toLowerCase()
        : true
      const matchesPorcentaje =
        item.porcentaje >= porcentajeRange[0] && item.porcentaje <= porcentajeRange[1]

      const itemDate = dayjs(item.fecha).format('YYYY-MM-DD')
      const matchesDate =
        (!tempFilters.fechaInicio || itemDate >= tempFilters.fechaInicio) &&
        (!tempFilters.fechaFin || itemDate <= tempFilters.fechaFin)

      return matchesKeyword && matchesBank && matchesPorcentaje && matchesDate
    })

    if (!valoresAprox) return filteredRows

    return [...filteredRows].sort((a, b) => {
      const diffPorcentaje = b.porcentaje - a.porcentaje
      if (diffPorcentaje !== 0) return diffPorcentaje
      const diffA = Math.abs(dayjs(a.fecha).diff(dayjs(dataModal?.fecha), 'day'))
      const diffB = Math.abs(dayjs(b.fecha).diff(dayjs(dataModal?.fecha), 'day'))
      return diffA - diffB
    })
  }

  if (isPartialConciliation) {
    filteredRows = applyFilters(
      bankRows.filter(item => {
        const status = item.status
        return (
          linkageComprobantes.includes(item.comprobante) ||
          status === 'PENDIENTE' ||
          status === 'PARCIALMENTE CONCILIADO'
        )
      })
    )
  } else {
    filteredRows = applyFilters(
      bankRows.filter(item => {
        const status = item.status
        return (
          status === 'PENDIENTE' ||
          status === 'PARCIALMENTE CONCILIADO' ||
          linkageComprobantes.includes(item.comprobante)
        )
      })
    )
  }

  if (isPartialConciliation) {
    const inLinkage = []
    const notInLinkage = []
    filteredRows.forEach(row => {
      if (linkageComprobantes.includes(row.comprobante)) inLinkage.push(row)
      else notInLinkage.push(row)
    })
    filteredRows = [...inLinkage, ...notInLinkage]
  }

  // Extraer linkageIds si existen
  const linkageFilters = useLinkageFilters(dataModal, {
    searchKeyword,
    porcentajeRange,
    tempFilters,
    total,
    linkageComprobantes,
    isPartialConciliation,
    valoresAprox
  })

  const {
    linkageIds,
    linkageIdsSet,
    filterItems,
    applyCompFilters,
    applyLinkageLogic,
    orderByLinkage,
    getRowIdentifiers
  } = linkageFilters

  let eg = applyCompFilters(applyLinkageLogic(filterItems(dataCompensacion.egreso)))
  let ing = applyCompFilters(applyLinkageLogic(filterItems(dataCompensacion.ingreso)))

  const filterByTcOrigin = rows =>
    rows.filter(row => {
      if (ENABLE_DIN_ANTICIPO && [27, 28].includes(Number(row?.folioCredito))) return false
      const origin = (row?._origen || row?.tipo || 'tc').toLowerCase()
      if (origin !== 'tc') return false
      const hasName = Boolean(row?.razonSocial)
      const hasAccount = Boolean(row?.nombreCuenta || row?.cuentaCorriente)
      const amount = Number(
        row?.montoTotal ??
          row?.montoRelacionado ??
          row?.montoAbono ??
          row?.montoCargo ??
          row?.montoEditable ??
          row?.debe ??
          row?.haber ??
          0
      )
      return hasName || hasAccount || amount !== 0
    })

  eg = filterByTcOrigin(eg)
  ing = filterByTcOrigin(ing)

  if (isPartialConciliation) {
    eg = orderByLinkage(eg)
    ing = orderByLinkage(ing)
  }

  const montoBase = esBoleta ? dataModal?.montoNeto : dataModal?.montoTotal

  const filteredCompensacionFinal = {
    egreso: eg,
    ingreso: ing,
    seleccionado: dataCompensacion.seleccionado
  }

  useEffect(() => {
    if (!dataModal?.linkage?.length || linkageIdsSet.size === 0) return
    const linkageArray = Array.from(linkageIdsSet)
    if (linkageArray.length === 0) return

    const egresoLoaded = new Set(allEgresos.flatMap(getRowIdentifiers))
    const ingresoLoaded = new Set(allIngresos.flatMap(getRowIdentifiers))

    const missingEgresos = linkageArray.some(key => !egresoLoaded.has(String(key).trim()))
    const missingIngresos = linkageArray.some(key => !ingresoLoaded.has(String(key).trim()))

    if (missingEgresos && hasNextEgresos && !isFetchingNextEgresos) {
      fetchNextEgresos()
    }
    if (missingIngresos && hasNextIngresos && !isFetchingNextIngresos) {
      fetchNextIngresos()
    }
  }, [
    dataModal?.linkage,
    linkageIds,
    allEgresos,
    allIngresos,
    hasNextEgresos,
    hasNextIngresos,
    isFetchingNextEgresos,
    isFetchingNextIngresos,
    fetchNextEgresos,
    fetchNextIngresos
  ])

  // ==========
  // RENDER DINÁMICO DE SECCIONES EN ORDEN DE CHECKS
  // ==========
  const orderedSections = []
  safeOrigen.forEach(v => {
    if (v === 'banco') orderedSections.push('banco')
    else if (v === 'tc') orderedSections.push('tc')
    else if (v === 'anticipo') orderedSections.push('anticipo')
    else if (v === 'caja') orderedSections.push('caja')
    else if (v === 'gestion') orderedSections.push('gestion')
  })

  // Banco con paginación si aplica
  // Priorizar solo los vinculos guardados para mantener las filas en su
  // lugar al seleccionar o desmarcar movimientos durante la edicion.
  const isPriorityBankRow = row =>
    getRowIdentifiers(row).some(identifier => linkageIdsSet.has(identifier))

  const sortedFilteredRows = [...filteredRows].sort((a, b) => {
    const priorityDifference = Number(isPriorityBankRow(b)) - Number(isPriorityBankRow(a))
    if (priorityDifference !== 0) return priorityDifference

    if (valoresAprox) {
      const porcentajeA = Number(a.porcentaje ?? 0)
      const porcentajeB = Number(b.porcentaje ?? 0)
      const differenceBySimilarity = porcentajeB - porcentajeA
      if (differenceBySimilarity !== 0) return differenceBySimilarity

      return dayjs(a.fecha).valueOf() - dayjs(b.fecha).valueOf()
    }

    if (!order.field) return 0

    const aValue = order.field === 'fecha' ? dayjs(a.fecha).valueOf() : a[order.field]
    const bValue = order.field === 'fecha' ? dayjs(b.fecha).valueOf() : b[order.field]

    if (aValue === bValue) return 0
    const comparison = aValue < bValue ? -1 : 1
    return order.direction === 'asc' ? comparison : -comparison
  })

  const paginatedData =
    rowsPerPage > 0
      ? sortedFilteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sortedFilteredRows

  const isSelectedView = val => safeOrigen.includes(val)

  const getSectionSelectedCount = key => {
    if (key === 'banco') {
      const selected = toArr(selectedDataByOrigen?.banco)
      const comprobantesCajaPrincipal = (dataModal?.linkage || [])
        .filter(it => it.nombreCuentaBanco === 'Caja Principal')
        .map(it => it.comprobante)
      return selected.filter(it => !comprobantesCajaPrincipal.includes(it.comprobante)).length
    }
    if (key === 'tc') return toArr(selectedDataByOrigen?.tc).length
    if (key === 'anticipo') return toArr(selectedDataByOrigen?.anticipo).length
    if (key === 'caja') return toArr(selectedDataByOrigen?.caja).length
    if (key === 'gestion') return toArr(selectedDataByOrigen?.gestion).length
    return 0
  }

  const renderSection = key => {
    const baseTitle =
      key === 'banco'
        ? 'Movimientos Bancarios'
        : key === 'tc'
          ? 'Movimientos de Compensación'
          : key === 'anticipo'
            ? 'Movimientos de Anticipo'
            : key === 'caja'
              ? 'Movimientos de Caja Principal'
              : key === 'gestion'
                ? 'Documento cobrado año anterior'
                : ''
    const selectedCount = getSectionSelectedCount(key)
    const headerTitle = selectedCount > 0 ? `${baseTitle} (${selectedCount})` : baseTitle

    if (key === 'banco' && !isSelectedView('banco')) return null
    if (key === 'tc' && !isSelectedView('tc')) return null
    if (
      key === 'anticipo' &&
      (!isSelectedView('anticipo') || vinculacionesAnticipoVisibles.length === 0)
    )
      return null
    if (key === 'caja' && !isSelectedView('caja')) return null
    if (key === 'gestion' && !isSelectedView('gestion')) return null

    return (
      <div key={`sec-${key}`} className='border border-gray-200 rounded-3xl bg-gray-100'>
        {/* Header clickeable */}
        <button
          type='button'
          onClick={() => toggleSection(key)}
          className='w-full flex items-center justify-between px-3 py-2'
          aria-expanded={!isCollapsed(key)}
          aria-controls={`sec-panel-${key}`}
        >
          <span className='text-sm font-sans font-medium'>{headerTitle}</span>
          <KeyboardArrowDownIcon
            className={clsx('transition-transform', { 'rotate-180': !isCollapsed(key) })}
          />
        </button>

        {/* Contenido colapsable */}
        <div
          id={`sec-panel-${key}`}
          className={clsx(
            'transition-[max-height] duration-300 ease-in-out overflow-hidden',
            isCollapsed(key) ? 'max-h-0' : 'flex-1 h-full'
          )}
        >
          <div className='p-2 pt-0'>
            {key === 'banco' && (
              <>
                <TablaAdmVinculacion
                onCheckboxChange={ids => handleCheckboxChange('banco', ids)}
                rows={paginatedData}
                idPrincipal={idPrincipal}
                dataLinkage={dataLinkage}
                setReloadData={setReloadData}
                resetSlider={resetSlider}
                dataModal={dataModal}
                selectedBankFilter={selectedBank}
                selectedIds={selectedIds.banco}
                setSelectedIds={ids => setSelectedIds(prev => ({ ...prev, banco: ids }))}
                unselectedIds={unselectedIds.banco}
                setUnselectedIds={updater => {
                  setUnselectedIds(prev => ({
                    ...prev,
                    banco: typeof updater === 'function' ? updater(prev.banco) : updater
                  }))
                }}
                setInitialSelectedIds={setInitialSelectedIds}
                modalStatus={modalStatus}
                modalLinkageList={dataModal?.linkage}
                onCurrentData={onCurrentDataBanco}
                onSort={handleSort}
                order={order}
                setForceShowAll={setForceShowAll}
                setUserInteracted={setUserInteracted}
                selectedCheckboxes={selectedCheckboxes}
                setSelectedCheckboxes={setSelectedCheckboxes}
                />
                {hasNextBankMovements && (
                  <div className='flex justify-center py-3'>
                    <button
                      type='button'
                      onClick={() => fetchNextBankMovements()}
                      disabled={isFetchingNextBankMovements}
                      className='rounded-full border border-gray-300 px-4 py-1.5 text-xs disabled:cursor-wait disabled:opacity-50'
                    >
                      {isFetchingNextBankMovements ? 'Cargando...' : 'Cargar más movimientos'}
                    </button>
                  </div>
                )}
              </>
            )}

            {key === 'tc' && origenSeleccionado.includes('tc') && (
              <>
                <TablaCompensacion
                  data={filteredCompensacionFinal}
                  idPrincipal={idPrincipal}
                  modalStatus={modalStatus}
                  setReloadData={setReloadData}
                  selectedIds={selectedIds.tc}
                  setSelectedIds={ids => setSelectedIds(prev => ({ ...prev, tc: ids }))}
                  unselectedIds={unselectedIds.tc}
                  setUnselectedIds={updater => {
                    setUnselectedIds(prev => ({
                      ...prev,
                      tc: typeof updater === 'function' ? updater(prev.tc) : updater
                    }))
                  }}
                  setInitialSelectedIds={setInitialSelectedIds}
                  dataLinkage={dataLinkage}
                  dataModal={dataModal}
                  onSort={handleSort}
                  order={order}
                  onCurrentData={onCurrentDataTC}
                  onCheckboxChange={ids => handleCheckboxChange('tc', ids)}
                  selectedCheckboxes={selectedCheckboxes}
                  setSelectedCheckboxes={setSelectedCheckboxes}
                  selectedDataByOrigen={selectedDataByOrigen}
                  setSelectedDataByOrigen={setSelectedDataByOrigen}
                  montoBase={montoBase}
                />
                {hasMoreCompPages && (
                  <div ref={lastElementRef} className='flex justify-center py-4'>
                    {isFetchingMoreCompensacion && (
                      <Lottie animationData={LoaderAdm} loop style={{ width: 80, height: 80 }} />
                    )}
                  </div>
                )}
              </>
            )}

            {ENABLE_DIN_ANTICIPO &&
              key === 'anticipo' &&
              vinculacionesAnticipoVisibles.length > 0 &&
              origenSeleccionado.includes('anticipo') && (
                <TablaVinculacionesAnticipo
                  rows={vinculacionesAnticipoVisibles}
                  selectedIds={selectedIds.anticipo}
                  onSelectionChange={(ids, rows) => {
                    console.log('[ModalEditConciliar] Anticipos seleccionados:', {
                      ids,
                      movimientos: rows
                    })
                    const selectedIdSet = new Set(ids.map(id => String(id)))
                    const anticipoTableIds = new Set(
                      vinculacionesAnticipoVisibles.map(row => String(row?.idPrincipal ?? row?.id))
                    )
                    const linkedAnticipoIds = toArr(dataModal?.linkage)
                      .map(item => item?.idPrincipal ?? item?.id)
                      .filter(id => id != null && anticipoTableIds.has(String(id)))

                    setUnselectedIds(prev => {
                      const current = toArr(prev.anticipo)
                      let next = [...current]
                      linkedAnticipoIds.forEach(id => {
                        if (selectedIdSet.has(String(id))) {
                          next = next.filter(value => String(value) !== String(id))
                        } else if (!next.some(value => String(value) === String(id))) {
                          next.push(id)
                        }
                      })
                      return { ...prev, anticipo: next }
                    })
                    setSelectedIds(prev => ({ ...prev, anticipo: ids }))
                    onCurrentDataTC?.(
                      rows.map(row => ({ ...row, _origen: 'anticipo' })),
                      'anticipo'
                    )
                  }}
                />
              )}

            {key === 'caja' && (
              <TableCajaPrincipal
                key='tabla-caja'
                onCheckboxChange={ids => handleCheckboxChange('caja', ids)}
                rows={filteredRows}
                idPrincipal={idPrincipal}
                dataLinkage={dataLinkage}
                setReloadData={setReloadData}
                resetSlider={resetSlider}
                dataModal={dataModal}
                selectedBankFilter={selectedBank}
                origenSeleccionado={['caja']}
                modalStatus={modalStatus}
                onCurrentData={rows => onCurrentDataCaja?.(rows)}
                selectedCheckboxes={selectedCheckboxes}
                setSelectedCheckboxes={setSelectedCheckboxes}
                selectedDataByOrigen={selectedDataByOrigen}
                setSelectedDataByOrigen={setSelectedDataByOrigen}
                selectedIds={selectedIds.caja}
                setSelectedIds={ids => setSelectedIds(prev => ({ ...prev, caja: ids }))}
                unselectedIds={unselectedIds.caja}
                setUnselectedIds={updater => {
                  setUnselectedIds(prev => ({
                    ...prev,
                    caja: typeof updater === 'function' ? updater(prev.caja) : updater
                  }))
                }}
              />
            )}

            {key === 'gestion' && (
              <TableCajaPrincipal
                key='tabla-gestion'
                onCheckboxChange={ids => handleCheckboxChange('gestion', ids)}
                rows={filteredRows}
                idPrincipal={idPrincipal}
                dataLinkage={dataLinkage}
                setReloadData={setReloadData}
                resetSlider={resetSlider}
                dataModal={dataModal}
                selectedBankFilter={selectedBank}
                origenSeleccionado={['gestion']}
                modalStatus={modalStatus}
                onCurrentData={rows => onCurrentDataGestion?.(rows)}
                selectedCheckboxes={selectedCheckboxes}
                setSelectedCheckboxes={setSelectedCheckboxes}
                selectedDataByOrigen={selectedDataByOrigen}
                setSelectedDataByOrigen={setSelectedDataByOrigen}
                selectedIds={selectedIds.gestion}
                setSelectedIds={ids => setSelectedIds(prev => ({ ...prev, gestion: ids }))}
                unselectedIds={unselectedIds.gestion}
                setUnselectedIds={updater => {
                  setUnselectedIds(prev => ({
                    ...prev,
                    gestion: typeof updater === 'function' ? updater(prev.gestion) : updater
                  }))
                }}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  const labelTexto = ['COBRADO', 'POR COBRAR', 'PARCIALMENTE COBRADO'].includes(modalStatus)
    ? 'Cobro con Caja Principal'
    : ['PAGADO', 'POR PAGAR', 'PARCIALMENTE PAGADO'].includes(modalStatus)
      ? 'Conciliar con Caja Principal'
      : ''

  const labelFiltro = ['COBRADO', 'POR COBRAR', 'PARCIALMENTE COBRADO'].includes(modalStatus)
    ? 'Tipo de Cobro'
    : ['PAGADO', 'POR PAGAR', 'PARCIALMENTE PAGADO'].includes(modalStatus)
      ? 'Tipo de Pago'
      : ''

  const labelGestion = ['COBRADO', 'POR COBRAR', 'PARCIALMENTE COBRADO'].includes(modalStatus)
    ? 'Documento Cobrado año anterior'
    : ['PAGADO', 'POR PAGAR', 'PARCIALMENTE PAGADO'].includes(modalStatus)
      ? 'Documento Pagado año anterior'
      : ''

  const opciones = [
    { value: 'caja', label: labelTexto },
    { value: 'gestion', label: labelGestion },
    { value: 'tc', label: 'Conciliar con Compensación' },
    { value: 'banco', label: 'Conciliar con Bancos' }
  ]

  useEffect(() => {
    if (!dataModal?.idPrincipal) return

    const linkages = Array.isArray(dataModal?.linkage) ? dataModal.linkage : []
    const originKey = JSON.stringify({
      idPrincipal: dataModal.idPrincipal,
      linkages: linkages.map(item => ({
        id: item?.id ?? item?.idPrincipal,
        comprobante: item?.comprobante,
        originTable: item?.libroContableOriginTableId ?? item?.tablaPrincipal ?? item?.tabla,
        descripcion: item?.descripcion
      }))
    })
    if (initializedOriginKeyRef.current === originKey) return

    if (linkages.length === 0) {
      setOrigenSeleccionado(['banco'])
      initializedOriginKeyRef.current = originKey
      return
    }

    const linkageComprobantes = linkages.map(item => String(item?.comprobante ?? '').trim())
    const bancoComprobantes = allData.map(item => String(item?.comprobante ?? '').trim())
    const egresoComprobantes = (dataCompensacion.egreso || [])
      .map(item => String(item?.comprobante ?? '').trim())
    const ingresoComprobantes = (dataCompensacion.ingreso || [])
      .map(item => String(item?.comprobante ?? '').trim())

    const isCajaPrincipalLinkage = item =>
      [item?.nombreCuentaBanco, item?.nombreBanco, item?.name]
        .some(value => String(value ?? '').trim().toLowerCase() === 'caja principal')
    const hasCajaPrincipal = linkages.some(isCajaPrincipalLinkage)
    const hasGestion = linkages.some(isPreviousYearConciliationLinkage)
    const isBankLinkage = item =>
      [item?.libroContableOriginTableId, item?.tablaPrincipal, item?.tabla]
        .some(value => [6, 7].includes(Number(value))) &&
      !isCajaPrincipalLinkage(item)

    const matchBanco = linkages.some(isBankLinkage) ||
      linkageComprobantes.some(c => bancoComprobantes.includes(c))
    const matchTC = linkages.some(item => {
      if (
        isBankLinkage(item) ||
        isCajaPrincipalLinkage(item) ||
        isPreviousYearConciliationLinkage(item)
      ) return false
      const comprobante = String(item?.comprobante ?? '').trim()
      return egresoComprobantes.includes(comprobante) || ingresoComprobantes.includes(comprobante)
    })

    const origenes = new Set()
    if (matchBanco) origenes.add('banco')
    if (matchTC) origenes.add('tc')
    if (!matchBanco && !matchTC) origenes.add('banco')
    if (hasCajaPrincipal) origenes.add('caja')
    if (hasGestion) origenes.add('gestion')

    setOrigenSeleccionado([...origenes])
    initializedOriginKeyRef.current = originKey
  }, [dataModal?.idPrincipal, dataModal?.linkage, data, dataCompensacion.egreso, dataCompensacion.ingreso])

  useEffect(() => {
    if (safeOrigen.length === 0) return
    const ultimo = safeOrigen[safeOrigen.length - 1]
    const mapKey = origen => {
      if (origen === 'banco') return 'banco'
      if (origen === 'tc') return 'tc'
      if (origen === 'anticipo') return 'anticipo'
      if (origen === 'caja' || origen === 'gestion') return 'caja-gestion'
      return null
    }
    const ultimaKey = mapKey(ultimo)
    if (!ultimaKey) return
    setCollapsed(prev => {
      const next = { ...prev }
      ;['banco', 'tc', 'anticipo', 'caja-gestion'].forEach(k => {
        next[k] = k !== ultimaKey // true = colapsado
      })
      return next
    })
  }, [safeOrigen])

  return (
    <div className='h-full p-3 bg-white rounded-3xl shadow-md flex flex-col overflow-hidden'>
      {isLoadingData ? (
        <div className='flex items-center justify-center h-64'>
          <Lottie animationData={LoaderAdm} loop style={{ width: 200, height: 200 }} />
        </div>
      ) : (
        <>
          <div className='border border-gray-200 rounded-3xl p-2 bg-gray-50'>
            <div className='flex flex-wrap gap-x-2 gap-y-4 items-start'>
              <div className='flex flex-wrap gap-x-2 gap-y-4 items-start flex-1'>
                <div className='min-w-[150px] flex flex-col justify-end mt-5 font-medium'>
                  <FilterDateConciliar
                    label='Fecha Desde'
                    value={tempFilters.fechaInicio}
                    onChange={newValue => handleDateChange('fechaInicio', newValue)}
                  />
                </div>
                <div className='min-w-[150px] flex flex-col justify-end mt-5 font-medium'>
                  <FilterDateConciliar
                    label='Fecha Hasta'
                    value={tempFilters.fechaFin}
                    onChange={newValue => handleDateChange('fechaFin', newValue)}
                  />
                </div>
                <div className='min-w-[150px]'>
                  <label className='block mb-[1px] mt-1 text-[11px] font-small leading-tight font-medium'>
                    Bancos
                  </label>
                  <select
                    className='h-7 w-full appearance-none focus:outline-none border border-gray-300 px-4 py-[3px] rounded-full leading-tight text-[11px] font-medium'
                    onChange={e => setSelectedBank(e.target.value)}
                    value={selectedBank}
                  >
                    <option value=''>Todos</option>
                    {bankOptions
                      .filter(bankName => bankName !== 'Caja Principal')
                      .map(bankName => (
                        <option key={bankName} value={bankName}>
                          {bankName}
                        </option>
                      ))}
                  </select>
                </div>
                <div className='min-w-[150px] font-medium'>
                  <InputField
                    label='Palabra Clave'
                    type='text'
                    value={searchKeyword}
                    onChange={e => setSearchKeyword(e.target.value)}
                  />
                </div>
                <label className='mt-5 inline-flex h-7 cursor-pointer items-center gap-2 text-[11px] font-medium'>
                  <input
                    type='checkbox'
                    checked={valoresAprox}
                    onChange={e => setValoresAprox(e.target.checked)}
                    className='rounded'
                  />
                  Monto Similares
                </label>
                {showFilters && (
                  <div id='filters-panel' className='contents'>
                    {/*<div className='min-w-[150px] flex flex-col'>
                      <label className=' text-[11px] font-medium text-gray-700 leading-tight'>
                        Coincidencia Porcentaje
                      </label>
                      <Slider
                        value={porcentajeRange}
                        onChange={(e, newValue) => setPorcentajeRange(newValue)}
                        valueLabelDisplay='auto'
                        valueLabelFormat={value => `${value}%`}
                        min={0}
                        max={100}
                        sx={{
                          height: 4,
                          '& .MuiSlider-thumb': { width: 14, height: 14 },
                          '& .MuiSlider-track': { height: 4 },
                          '& .MuiSlider-rail': { height: 4 },
                          '& .MuiSlider-valueLabel': { fontSize: '10px', padding: '2px 4px' }
                        }}
                      />
                      <div className='text-[11px] text-gray-600 mt-1'>
                        {porcentajeRange[0]}% - {porcentajeRange[1]}%
                      </div>
                    </div>*/}
                    <div className='min-w-[200px] font-medium'>
                      <label className='block mb-[1px]  text-[11px] font-small text-gray-700 leading-tight'>
                        {labelFiltro}
                      </label>
                      <div className='grid grid-cols-2 gap-x-4 gap-y-2 px-3 py-1 text-[11px]'>
                        {opciones.map(option => (
                          <label key={option.value} className='flex items-center gap-2'>
                            <input
                              type='checkbox'
                              className='rounded'
                              value={option.value}
                              checked={origenSeleccionado.includes(option.value)}
                              onChange={e => {
                                const checked = e.target.checked
                                const origen = option.value
                                setOrigenSeleccionado(prev => {
                                  const updated = checked
                                    ? [...prev, origen]
                                    : prev.filter(val => val !== origen)
                                  return updated
                                })
                                if (!checked) {
                                  const idsToRemove = (selectedDataByOrigen[origen] || []).map(
                                    r => r.id
                                  )
                                  setUnselectedIds(prev => ({
                                    ...prev,
                                    [origen]: idsToRemove
                                  }))
                                  setSelectedDataByOrigen(prev => {
                                    const next = { ...prev }
                                    delete next[origen]
                                    return next
                                  })
                                  setSelectedIds(prev => {
                                    const next = { ...prev }
                                    delete next[origen]
                                    return next
                                  })
                                  setSelectedCheckboxes(prev => {
                                    const next = { ...prev }
                                    idsToRemove.forEach(id => delete next[id])
                                    return next
                                  })
                                } else {
                                  setUnselectedIds(prev => {
                                    const next = { ...prev }
                                    delete next[origen]
                                    return next
                                  })
                                }
                              }}
                            />
                            {option.label}
                          </label>
                        ))}
                        {/* {ENABLE_DIN_ANTICIPO && esDin && (
                          <label className='flex items-center gap-2'>
                            <input
                              type='checkbox'
                              className='rounded'
                              checked={conciliarConAnticipo}
                              onChange={e => {
                                const checked = e.target.checked
                                setConciliarConAnticipo?.(checked)
                                setOrigenSeleccionado(prev =>
                                  checked
                                    ? [...new Set([...prev, 'anticipo'])]
                                    : prev.filter(value => value !== 'anticipo')
                                )
                                if (!checked) {
                                  const idsToRemove = (selectedDataByOrigen.anticipo || []).map(
                                    row => row.idPrincipal ?? row.id
                                  )
                                  setUnselectedIds(prev => ({ ...prev, anticipo: idsToRemove }))
                                  setSelectedDataByOrigen(prev => {
                                    const next = { ...prev }
                                    delete next.anticipo
                                    return next
                                  })
                                  setSelectedIds(prev => {
                                    const next = { ...prev }
                                    delete next.anticipo
                                    return next
                                  })
                                }
                              }}
                            />
                            Conciliar con Anticipo
                          </label>
                        )} */}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className='ml-auto mt-5'>
                <button
                  type='button'
                  className='flex items-center px-1 py-0.5 bg-gray-200 rounded hover:bg-gray-300 transition'
                  onClick={() => setShowFilters(prev => !prev)}
                  aria-expanded={showFilters}
                  aria-controls='filters-panel'
                >
                  <KeyboardArrowDownIcon
                    className={clsx('transition-transform', { 'rotate-180': showFilters })}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Secciones ordenadas + scroll */}
          <div className='flex-1 min-h-0 mt-3 overflow-y-auto overflow-x-hidden'>
            <div className='flex flex-col space-y-3'>
              {orderedSections.map(secKey => renderSection(secKey))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ListEditVincular
